import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	achievementsGlow,
	GOLD_BUTTON_FILL,
	RADIUS_CARD,
	RADIUS_PANEL,
} from './achievementsTheme';
import { achievementText } from './achievementsViewModel';

const DIALOG = { width: 302, paddingTop: 24, paddingX: 20, paddingBottom: 20, gap: 12 };
const ALERT_SIZE = 48;
const BUTTON_HEIGHT = 44;

type Props = {
	onClose: () => void;
	scale: number;
};

export const MaxSelectedDialog = ({ onClose, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const panelFillId = useSvgId('achievementDialogPanel');
	const buttonFillId = useSvgId('achievementDialogButton');

	return (
		<View style={styles.root}>
			<Pressable accessibilityRole='button' style={styles.scrim} onPress={onClose} />

			<View accessibilityRole='alert' style={styles.dialog}>
				<View pointerEvents='none' style={styles.dialogPlate}>
					<Svg width='100%' height='100%'>
						<Defs>
							<LinearGradient id={panelFillId} x1='0' y1='0' x2='0' y2='1'>
								<Stop offset='0' stopColor={achievementsColors.dialogTop} />
								<Stop offset='1' stopColor={achievementsColors.dialogBottom} />
							</LinearGradient>
						</Defs>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${panelFillId})`} />
					</Svg>
				</View>

				<View style={styles.alertMark}>
					<Text style={styles.alertGlyph}>!</Text>
				</View>
				<Text style={styles.title}>{achievementText(t, 'T_SITE_ACHIEVEMENTS_MAX_TITLE')}</Text>
				<Text style={styles.body}>{achievementText(t, 'T_SITE_ACHIEVEMENTS_MAX_BODY')}</Text>

				<Pressable accessibilityRole='button' onPress={onClose} style={styles.button}>
					<View pointerEvents='none' style={styles.buttonPlate}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={buttonFillId} x1='0' y1='0' x2='0' y2='1'>
									{GOLD_BUTTON_FILL.map(([offset, color]) => (
										<Stop key={offset} offset={offset} stopColor={color} />
									))}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${buttonFillId})`} />
						</Svg>
					</View>
					<Text numberOfLines={1} style={styles.buttonLabel}>
						{achievementText(t, 'T_SITE_ACHIEVEMENTS_OK')}
					</Text>
				</Pressable>
			</View>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		root: {
			...StyleSheet.absoluteFillObject,
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 10,
		},
		scrim: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: achievementsColors.dialogScrim,
		},
		dialog: {
			width: DIALOG.width * scale,
			alignItems: 'center',
			gap: DIALOG.gap * scale,
			paddingTop: DIALOG.paddingTop * scale,
			paddingHorizontal: DIALOG.paddingX * scale,
			paddingBottom: DIALOG.paddingBottom * scale,
			borderRadius: RADIUS_PANEL * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelBorder,
			...achievementsGlow.dialog,
		},
		dialogPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_PANEL * scale,
			overflow: 'hidden',
		},
		alertMark: {
			width: ALERT_SIZE * scale,
			height: ALERT_SIZE * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: (ALERT_SIZE / 2) * scale,
			borderWidth: Math.max(1, 2 * scale),
			borderColor: achievementsColors.gold200,
			backgroundColor: achievementsColors.surface,
		},
		alertGlyph: {
			color: achievementsColors.gold200,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 26 * scale,
			fontWeight: '900',
			lineHeight: 26 * 1.2 * scale,
		},
		title: {
			width: '100%',
			color: achievementsColors.muted,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '700',
			lineHeight: 16 * 1.2 * scale,
			textAlign: 'center',
		},
		body: {
			width: '100%',
			color: achievementsColors.secondary,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
			lineHeight: 12 * 1.4 * scale,
			textAlign: 'center',
		},
		button: {
			width: '100%',
			height: BUTTON_HEIGHT * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_CARD * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.goldStroke,
			...achievementsGlow.gold,
		},
		buttonPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_CARD * scale,
			overflow: 'hidden',
		},
		buttonLabel: {
			color: achievementsColors.onGold,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 18 * scale,
			fontWeight: '900',
			lineHeight: 18 * 1.3 * scale,
			letterSpacing: 0.36 * scale,
		},
	});

export default MaxSelectedDialog;
